/**
 * studio-shop D1 / web-studio-sites D14 — the plug-in standard.
 *
 * Every capability a studio site can use is ONE plug-in built in Telify that
 * serves every studio, switched on per studio. Its declaration is the one place
 * it is listed: its switch, its website pieces, Telify's pages it draws, its
 * routes, admin sections, notice types, client messages and email kinds. Its
 * code follows the repo's boundaries (pieces in the kit's folder, a server
 * module, worker processors); the declaration only names them.
 *
 * Checked both ways against what is really built: `pnpm plugins:check` (in
 * `pnpm typecheck`) for the pieces, pages, notice types, client messages and
 * email kinds; the API's boot for the routes (`registerPluginRoutes`).
 *
 * Pure and browser-safe: type-only imports, so the API, the worker and the
 * webapp read one list.
 */
import type { ClientIntentType } from '../client-outbox/client-outbox.repository.js';
import type { AdminNotificationType } from '../telegram/admin-notification-types.js';
import type { TenantFeature } from '../tenant-context/tenant-features.js';
import type { WebPage } from '../web/paths.js';
import type { SiteOption } from './site-options.js';

export interface PluginRoute {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** The full address as the API registers it, for example `/api/site/services/:id`. */
  readonly url: string;
  /** Registered only outside production (a development sign-in), so it may be absent at boot. */
  readonly devOnly?: true;
  /**
   * studio-shop D1, off stops selling and keeps servicing: still answers while
   * the switch is off, for a studio the plug-in's `serviceable` check says yes
   * to (its existing work, such as orders). A GET's HEAD twin follows it.
   */
  readonly servicing?: true;
}

export interface PluginRoutes {
  /**
   * Every route under this prefix belongs to the plug-in: it must be registered
   * through `registerPluginRoutes` and declared below, and it is refused while
   * the plug-in's switch is off (a servicing route only when the studio is not
   * serviceable). A longer prefix of another plug-in wins.
   */
  readonly prefix: string;
  readonly routes: readonly PluginRoute[];
}

export interface PluginDeclaration {
  /** Kebab-case. Stamped on its routes and listed in the studio read's `plugins`. */
  readonly id: string;
  /** Its name in the plug-in catalog. */
  readonly name: string;
  /** What it gives a studio, in a sentence or two. */
  readonly description: string;
  /** Shown beside the switch and in the catalog. Telify has no billing yet, so the mark is information only. */
  readonly paidAddOn: boolean;
  /**
   * form-embed 3.0: the paid plug-in this one comes with, by id, shown as "Comes with" in the catalog
   * (Gabriel 2026-10-26: the forms and email signup plug-ins come with the Shop). Information only, like
   * `paidAddOn`: its own switch stays its only gate. Absent for a plug-in that stands alone.
   */
  readonly includedWith?: string;
  /** The studio switch that turns it on (`pnpm tenant:features`). */
  readonly feature: TenantFeature;
  /** Its website pieces, by contract name (`apps/webapp/src/web/pieces/<name>/`). */
  readonly pieces: readonly string[];
  /** Telify's own pages it draws on a studio address. */
  readonly pages: readonly WebPage['page'][];
  /** Its routes on a studio address. */
  readonly siteRoutes: PluginRoutes | null;
  /** Its routes in the Mini App admin's API. */
  readonly adminRoutes: PluginRoutes | null;
  /** Its sections in the Mini App admin. */
  readonly adminSections: readonly string[];
  /** The studio's Telegram notices it writes. A plug-in that declares one owns every `<id>.` notice type. */
  readonly noticeTypes: readonly AdminNotificationType[];
  /** The client messages it sends. A plug-in that declares one owns every `<id>_` message type. */
  readonly clientMessageTypes: readonly ClientIntentType[];
  /** The emails it sends, by kind. */
  readonly emailKinds: readonly string[];
  /** Its outside services (the shop's fulfilment services). */
  readonly services: readonly PluginService[];
  /**
   * web-studio-sites D14: words a site can set for itself in `site.json`'s `plugins` block, by option name, each with
   * its default (`site-options.ts`).
   */
  readonly siteOptions?: Readonly<Record<string, SiteOption>>;
}

/** studio-shop D1: one outside service a plug-in works with. */
export interface PluginService {
  /** Kebab-case, unique within the plug-in. */
  readonly id: string;
  /** Its name in the admin and the plug-in catalog. */
  readonly name: string;
  /** What it does for the studio. */
  readonly description: string;
  /** How a studio connects it: through the service's own sign-in, or nothing to connect. */
  readonly connect: 'oauth' | 'none';
  /** Shown beside it; information only until Telify has billing. */
  readonly paidAddOn: boolean;
}

/** Identity helper that keeps the literal types of a declaration. */
export function definePlugin<const P extends PluginDeclaration>(plugin: P): P {
  return plugin;
}
